/* eslint-disable import/no-unresolved */
// import "@aws-amplify/ui-react/styles.css";
import { FaceLivenessDetector } from "@aws-amplify/ui-react-liveness";
import { Amplify } from "aws-amplify";
import awsexports from "../utils/aws-exports";
import React from "react";
import { getLiveSelfiDetails, getSessionData, getShadowRoot } from "../utils/store";
import { DeclinedTheConsentForm, getConsent } from "../utils/StepService";
import { setLoading as setMainLoading } from "../utils/index";
const { forwardRef, useImperativeHandle } = React;

Amplify.configure(awsexports);

const isTabletDevice = () => {
  const userAgent = navigator.userAgent || "";
  const isIpad = /iPad/i.test(userAgent);
  const isAndroidTablet =
    /Android/i.test(userAgent) && !/Mobile/i.test(userAgent);
  const isTouchMac =
    /Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1;

  return isIpad || isAndroidTablet || isTouchMac;
};

const createPortraitOnlyMediaQueryList = (query) => ({
  matches: false,
  media: query,
  onchange: null,
  addEventListener: () => {},
  removeEventListener: () => {},
  addListener: () => {},
  removeListener: () => {},
  dispatchEvent: () => false,
});

export const AwsLiveness = forwardRef((props, ref) => {
  const [loading, setLoading] = React.useState(true);
  const [showLiveness, toggleLiveness] = React.useState(false);
  const [isSuccess, setSuccess] = React.useState(false);
  const [isError, setError] = React.useState(false);
  const [livenessSessionId, setCreateLivenessApiData] = React.useState(null);
  const [orientationPatchReady, setOrientationPatchReady] = React.useState(false);
  const originalMatchMediaRef = React.useRef(null);
  const isTablet = React.useMemo(() => isTabletDevice(), []);

  React.useEffect(() => {
    let selfieVerification = getShadowRoot().querySelector(`#${props.formId}`);

    let checkbox = selfieVerification.querySelector("#agreeCheckbox");
    if (checkbox) {
      selfieVerification.querySelector("#btn-continue").disabled = true;
      selfieVerification.querySelector("#canConsent").disabled = true;

      let consentText = selfieVerification.querySelector("#consentText").innerText;
      if (consentText.length < 2) {
        let consentDetails=getLiveSelfiDetails().content;
        if(consentDetails)
          {
          selfieVerification.querySelector("#canConsent").disabled = false;
          selfieVerification.querySelector("#consentText").innerHTML = " " + consentDetails;
        }
        else
          checkbox.disabled = true;
      }
      selfieVerification.querySelector("#agreeCheckbox").addEventListener("change", function () {
        selfieVerification.querySelector("#btn-continue").disabled = !this.checked;
      })
    }

    const fetchCreateLiveness = async () => {
      /*
       * This should be replaced with a real call to your own backend API
       */
      //   await new Promise((r) => setTimeout(r, 2000));
      const sessionData = getSessionData();
      if (sessionData?.livenessSessionId)
        setCreateLivenessApiData(sessionData.livenessSessionId);
      setLoading(false);
    };

    fetchCreateLiveness();
  }, [props.formId, showLiveness]);

  React.useLayoutEffect(() => {
    if (!showLiveness || !isTablet) {
      setOrientationPatchReady(false);
      return undefined;
    }

    const orientationQuery = "(orientation: landscape)";

    if (!originalMatchMediaRef.current && typeof window.matchMedia === "function") {
      originalMatchMediaRef.current = window.matchMedia.bind(window);
    }

    if (originalMatchMediaRef.current) {
      window.matchMedia = (query) => {
        if (query === orientationQuery) {
          return createPortraitOnlyMediaQueryList(query);
        }

        return originalMatchMediaRef.current(query);
      };
    }

    if (window.screen?.orientation?.lock) {
      window.screen.orientation.lock("portrait").catch(() => {});
    }

    setOrientationPatchReady(true);

    return () => {
      setOrientationPatchReady(false);

      if (originalMatchMediaRef.current) {
        window.matchMedia = originalMatchMediaRef.current;
      }

      if (window.screen?.orientation?.unlock) {
        window.screen.orientation.unlock();
      }
    };
  }, [isTablet, showLiveness]);

  useImperativeHandle(ref, () => ({
    startLiveness() {
      if (isError) setLivenessError(false);
      const sessionData = getSessionData();
      if (sessionData?.livenessSessionId)
        setCreateLivenessApiData(sessionData.livenessSessionId);
      toggleLiveness(true);
    },

    setLivenessError(val = true) {
      setError(val);
      if (val) setSuccess(false);
    },
  }));

  const cancleSession = async () => {
    setMainLoading(true);
    let consentDetails = await getConsent("LiveSelfieVerification");
    await DeclinedTheConsentForm(consentDetails);
    setMainLoading(false);
  };

  const handleAnalysisComplete = async () => {
    if (isError) setLivenessError(false);
    setSuccess(true);
    toggleLiveness(false);
    // setLoading(true);
    props.successCallback();
  };

  const onUserCancel = async () => {
    toggleLiveness(false);
    setLivenessError(false);
    props.toggleCameraClass(false);

    // show tryAnotherWay link
    const sessionData = getSessionData();
    if (sessionData?.isVerified) {
      let selfieVerification = getShadowRoot().querySelector(`#${props.formId}`);
      if (selfieVerification) {
        let tryAnotherWay = selfieVerification.querySelector("#tryAnotherWay");
        if (tryAnotherWay) tryAnotherWay.style.display = "block";
      }
    }
  };

  const errorCallback = async (errorObj) => {
    setLivenessError(true);
    toggleLiveness(false);
    if(errorObj.state === "CAMERA_ACCESS_ERROR")
      props.errorCallback("Oops! It seems that browser permission was denied previously. You can resolve this by navigating to your 'Website Settings -> Permissions' section and granting access to the camera. Once done, please retry the verification process.")
    else
      props.errorCallback(errorObj.error.message);
  };

  const setLivenessError = async (val = true) => {
    setError(val);
    if (val) setSuccess(false);
  };

  return (
    <>
      <div
        className="face-me-sdk-loading"
        id="faceMeSdkLoading"
        style={{ display: loading ? "flex" : "none" }}
      >
        <svg
          className="svg"
          width="100px"
          height="100px"
          aria-hidden="true"
          role="img"
        >
          <use
            href="#svg__loader"
            xmlnsXlink="http://www.w3.org/1999/xlink"
            xlinkHref="#svg__loader"
          ></use>
        </svg>
      </div>
      {showLiveness && (!isTablet || orientationPatchReady) ? (
        <FaceLivenessDetector
          sessionId={livenessSessionId}
          region={"us-east-1"}
          onAnalysisComplete={handleAnalysisComplete}
          onUserCancel={onUserCancel}
          disableInstructionScreen={true}
          onError={errorCallback}
        />
      ) : !isSuccess && !isError ? (
        <div>
          <div
            className="identity__outline-box identity__por identity__r-6"
            id="sdk_block"
            style={{
              minHeight: 260,
              width: "100%",
              cursor: "default",
              pointerEvents: "none",
              borderStyle: "solid",
              borderColor: "#224560",
              background: "linear-gradient(180deg, #082035 0%, #061828 100%)",
            }}
          >
            <div
              className="identity__outline-inner"
              style={{
                height: 350,
                padding: "26px 20px",
                flexDirection: "column",
                gap: 14,
              }}
            >
              <div
                className="identity__text-center"
                style={{
                  fontSize: 22,
                  lineHeight: 1.2,
                  fontWeight: 600,
                  color: "#ffffff",
                }}
              >
             Live Selfie Verification
              </div>
              <div
                style={{
                  width: 92,
                  height: 92,
                  borderRadius: "50%",
                  border: "1px solid #24558b",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "4px auto 0",
                  background: "rgba(12, 41, 68, 0.45)",
                }}
              >
                <svg
                  id="faceMeSdkCameraIcon"
                  className="svg"
                  width="52px"
                  height="52px"
                  aria-hidden="true"
                  role="img"
                >
                  <use
                    href="#svg__camera"
                    xmlnsXlink="http://www.w3.org/1999/xlink"
                    xlinkHref="#svg__camera"
                  ></use>
                </svg>
              </div>
              <div
                className="identity__text-center"
                style={{
                  maxWidth: 245,
                  fontSize: 16,
                  lineHeight: 1.45,
                  color: "#c8d8e8",
                }}
              >
                Your camera will open after you review and accept the consent below, then click "Start Camera".
              </div>
            </div>
          </div>
          <div className="identity__row" style={{ marginTop: 20 }}>
            <div className="identity__col-12">
              <div className="identity-field floating-field">
                <input type="checkbox" id="agreeCheckbox" className="identity-input-checkbox" />
                <label id="consentText"></label>
              </div>
            </div>
          </div>
          <div className="identity__col-12">
            <div className="identity-field">
              <button id="btn-continue" type="button" onClick={props.initializeLiveness}
                className="identity-btn btn-continue" >Start Camera
                <span className="loading-icon">
                </span>
              </button>
            </div>
          </div>
          <div className="identity__col-12">
            <div className="identity-field ">
              <button type="button" onClick={cancleSession}
                className="identity-btn identity-btn__secondary"
                id="canConsent">Cancel
                <span className="loading-icon">
                  <svg className="svg" width="20px" height="20px" aria-hidden="true" role="img">
                    <use
                      href="#svg__loader"
                      xmlnsXlink="http://www.w3.org/1999/xlink"
                      xlinkHref="#svg__loader"
                    ></use>
                  </svg>
                </span>
              </button>
            </div>
          </div>
          </div>
      ) : null}
    </>
  );
});

export default AwsLiveness;
